/**
 * Responsive Design Constants
 *
 * Centralized constants for breakpoints, sizes, and responsive behavior.
 * These should match the CSS media queries used throughout the application.
 */

/**
 * Responsive breakpoints (in pixels)
 * These match the media queries in CSS files
 */
export const BREAKPOINTS = {
  mobile: 480,
  tablet: 768,
  desktop: 1024,
  wide: 1280
};

/**
 * Floating button configuration
 * Used in FloatingThemeButton component
 */
export const FLOATING_BUTTON = {
  sizes: {
    mobile: {
      size: 40,
      margin: 12,
      radius: 18 // Proportional to 40px button
    },
    tablet: {
      size: 40,
      margin: 16,
      radius: 18 // Proportional to 40px button
    },
    desktop: {
      size: 60,
      margin: 32,
      radius: 29 // Proportional to 60px button
    }
  },
  footerBuffer: 20, // Distance from footer before hiding
  scrollThreshold: 100 // Pixels to scroll before showing button
};

/**
 * Animation durations (in milliseconds)
 * Keep these in sync with CSS animations
 */
export const ANIMATION_DURATION = {
  fast: 150,
  normal: 300,
  slow: 500,
  bounce: 2500
};

/**
 * Z-index layers
 * Maintain a consistent z-index hierarchy
 */
export const Z_INDEX = {
  base: 1,
  dropdown: 10,
  sticky: 100,
  fixed: 1000,
  modal: 10000,
  tooltip: 100000
};

/**
 * Helper function to get current breakpoint
 * @returns {string} - 'mobile' | 'tablet' | 'desktop' | 'wide'
 */
export function getCurrentBreakpoint() {
  const width = window.innerWidth;

  if (width <= BREAKPOINTS.mobile) return 'mobile';
  if (width <= BREAKPOINTS.tablet) return 'tablet';
  if (width <= BREAKPOINTS.desktop) return 'desktop';
  return 'wide';
}

/**
 * Helper function to get floating button size for current viewport
 * @returns {Object} - { size, margin, radius }
 */
export function getFloatingButtonSize() {
  const breakpoint = getCurrentBreakpoint();

  if (breakpoint === 'mobile') return FLOATING_BUTTON.sizes.mobile;
  if (breakpoint === 'tablet') return FLOATING_BUTTON.sizes.tablet;
  return FLOATING_BUTTON.sizes.desktop;
}

/**
 * Helper to check if viewport is mobile
 * @returns {boolean}
 */
export function isMobile() {
  return window.innerWidth <= BREAKPOINTS.mobile;
}

/**
 * Helper to check if viewport is tablet or smaller
 * @returns {boolean}
 */
export function isTabletOrSmaller() {
  return window.innerWidth <= BREAKPOINTS.tablet;
}
